import '@/styles/battlepass-layout.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assetUrl, itemIconFallbackUrl, resolveMediaUrl } from '@/lib/assetUrl';
import {
  filterGroupItemsByGender,
  waitAndInitBattlepassGroupPopover,
} from '@/lib/battlepassGroupPopover';
import { useLocale } from '@/context/LocaleContext';
import { IngameFlash, useIngameFlash } from '@/components/IngameFlash';
import { SimpleMenu } from '@/components/MenuGrid';
import {
  useBattlepassMutations,
  useBattlepassProgress,
  useBattlepassStatic,
  type BattlepassRewardCell,
} from '@/hooks/useBattlepass';
import { mergeBattlepassRewards, preloadBattlepassImages } from '@/lib/battlepassMerge';
import { useSilkBalance } from '@/hooks/useSilkBalance';
import { SilkConfirmModal, type SilkConfirmPhase } from '@/components/SilkConfirmModal';
import { BattlepassExpModal } from '@/components/BattlepassExpModal';

const BP_PREMIUM_COST = 1499;
const BP_LEVELUP_COST = 99;

type SilkSpendAction = 'premium' | 'levelup';

interface SilkSpendDialog {
  action: SilkSpendAction;
  phase: SilkConfirmPhase;
  message?: string;
}

function formatDaysRemaining(
  end: string,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const endDate = new Date(end);
  const now = new Date();
  const diffMs = Math.max(0, endDate.getTime() - now.getTime());
  const days = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  if (days === 1) return t('bp_day_remaining');
  return t('bp_days_remaining', { days });
}

function isGroupReward(reward: BattlepassRewardCell): boolean {
  return reward.has_multiple === true;
}

function RewardCell({
  reward,
  cellClass,
  userGender,
  progressReady,
  onClaim,
}: {
  reward: BattlepassRewardCell | null;
  cellClass: string;
  userGender: string | null | undefined;
  progressReady: boolean;
  onClaim: (rewardIds: number[]) => Promise<boolean>;
}) {
  const { t } = useLocale();
  const effectiveState = progressReady ? (reward?.state ?? '') : '';
  const [visualState, setVisualState] = useState(reward?.state ?? '');
  const [claimPhase, setClaimPhase] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const popTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setVisualState(progressReady ? (reward?.state ?? '') : '');
  }, [reward?.state, reward?.user_reward_id, progressReady]);

  useEffect(() => {
    return () => {
      if (popTimer.current) clearTimeout(popTimer.current);
    };
  }, []);

  const popoverInitKey = useMemo(() => {
    if (!reward || !isGroupReward(reward)) return '';
    const items = filterGroupItemsByGender(reward.group_items ?? [], userGender);
    return `${reward.user_reward_id}:${items.map((i) => i.user_reward_id).join(',')}:${userGender ?? 'A'}`;
  }, [reward, userGender]);

  useEffect(() => {
    const el = imgRef.current;
    if (!el || !reward || !popoverInitKey) return undefined;
    const items = filterGroupItemsByGender(reward.group_items ?? [], userGender);
    if (items.length === 0) return undefined;
    return waitAndInitBattlepassGroupPopover(el, items, userGender);
  }, [popoverInitKey, reward, userGender]);

  if (!reward) {
    return (
      <div className={`battlepass-carousel-cell ${cellClass}`}>
        <div className="battlepass-carousel-cell-imgbox" />
        <div className="battlepass-reward-status" aria-hidden />
      </div>
    );
  }

  const isClaimable = progressReady && effectiveState === 'claimable' && claimPhase === 'idle';
  const imgState = !progressReady
    ? 'neutral'
    : visualState === 'claimed' || claimPhase === 'success'
      ? 'claimed'
      : effectiveState || 'neutral';
  const groupItems = filterGroupItemsByGender(reward.group_items ?? [], userGender);
  const isGroup = isGroupReward(reward);
  const claimIds =
    isGroup && groupItems.length > 0
      ? groupItems.map((item) => item.user_reward_id)
      : [reward.user_reward_id];

  const imgClass = [
    'battlepass-carousel-cell-img',
    `bp-reward-img-${imgState}`,
    claimPhase === 'pending' ? 'reward-claim-absorb' : '',
    claimPhase === 'success' ? 'reward-claim-pop' : '',
    claimPhase === 'error' ? 'reward-claim-error' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const isClaimed = progressReady && (visualState === 'claimed' || claimPhase === 'success');
  const isLocked =
    progressReady &&
    !isClaimed &&
    (effectiveState === 'locked' ||
      effectiveState === 'not_unlocked' ||
      cellClass.includes('locked'));

  const cellStateClass = [
    cellClass,
    progressReady ? visualState : '',
    isClaimed ? 'claimed' : '',
    isLocked ? 'is-locked' : '',
    !progressReady ? 'battlepass-carousel-cell--awaiting' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = async () => {
    if (!isClaimable) return;

    setClaimPhase('pending');
    const ok = await onClaim(claimIds);

    if (ok) {
      setVisualState('claimed');
      setClaimPhase('success');
      popTimer.current = setTimeout(() => setClaimPhase('idle'), 700);
    } else {
      setClaimPhase('error');
      popTimer.current = setTimeout(() => setClaimPhase('idle'), 600);
    }
  };

  return (
    <div className={`battlepass-carousel-cell ${cellStateClass}`}>
      <div className="battlepass-carousel-cell-imgbox">
        <img
          ref={imgRef}
          src={resolveMediaUrl(reward.image_url)}
          alt={reward.SN_Name}
          className={imgClass}
          title={isGroup ? undefined : `${reward.SN_Name} x${reward.Quantity}`}
          style={{
            cursor: isClaimable ? 'pointer' : 'default',
            pointerEvents: claimPhase === 'pending' ? 'none' : undefined,
          }}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = itemIconFallbackUrl();
          }}
          onClick={() => void handleClick()}
        />
        {claimPhase === 'pending' && <div className="reward-claim-spinner" aria-hidden />}
        {isGroup && (
          <div className="battlepass-group-icon" title={t('bp_multiple_items')}>
            <i className="ph-light ph-stack" aria-hidden />
          </div>
        )}
        <div className="battlepass-quantity-badge">x{reward.Quantity}</div>
      </div>
      <div className="battlepass-reward-status">
        {isClaimed ? (
          <i className="ph-fill ph-check-circle battlepass-status-check" aria-label="claimed" />
        ) : isLocked ? (
          <i className="ph-fill ph-lock-simple battlepass-status-lock" aria-label="locked" />
        ) : (
          <span className="battlepass-status-spacer" aria-hidden />
        )}
      </div>
    </div>
  );
}

function BattlepassSkeletonCarousel({ cols = 8 }: { cols?: number }) {
  return (
    <div className="battlepass-carousel battlepass-carousel--loading" aria-hidden>
      {Array.from({ length: cols }, (_, i) => (
        <div className="battlepass-carousel-col" key={i}>
          <div className="bp-level-diamond-wrapper">
            <span className="bp-level-number">{i + 1}</span>
            <div className="bp-level-rail" aria-hidden>
              <span className="bp-level-diamond battlepass-skeleton-diamond" />
            </div>
          </div>
          <div className="battlepass-carousel-cell battlepass-carousel-cell--premium battlepass-skeleton-cell" />
          <div className="bp-track-separator" aria-hidden>
            <span className="bp-track-separator-diamond" />
          </div>
          <div className="battlepass-carousel-cell battlepass-carousel-cell--free battlepass-skeleton-cell" />
        </div>
      ))}
    </div>
  );
}

export function BattlepassPage() {
  const { t } = useLocale();
  const {
    data: staticData,
    isLoading: staticLoading,
    isError: staticError,
  } = useBattlepassStatic();
  const {
    data: progressData,
    isLoading: progressLoading,
    isError: progressError,
  } = useBattlepassProgress();
  const { claimMany, buyPremium, levelUp } = useBattlepassMutations();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [expModalOpen, setExpModalOpen] = useState(false);
  const [silkDialog, setSilkDialog] = useState<SilkSpendDialog | null>(null);
  const { flash, showFlash } = useIngameFlash(3000);
  const silkModalOpen = silkDialog?.phase === 'confirm';
  const { data: silkBalance, isFetching: silkLoading, isError: silkError } = useSilkBalance(silkModalOpen);

  const progressReady = progressData != null;
  const progressInitialLoad = progressLoading && progressData == null;
  const bp = staticData?.battlepass ?? null;
  const maxLevels = progressData?.maxLevels ?? staticData?.maxLevels ?? 0;

  const freeRewards = useMemo(
    () =>
      mergeBattlepassRewards(
        staticData?.freeRewards,
        progressData?.freeRewards,
        maxLevels,
        progressReady,
      ),
    [staticData?.freeRewards, progressData?.freeRewards, maxLevels, progressReady],
  );

  const premiumRewards = useMemo(
    () =>
      mergeBattlepassRewards(
        staticData?.premiumRewards,
        progressData?.premiumRewards,
        maxLevels,
        progressReady,
      ),
    [staticData?.premiumRewards, progressData?.premiumRewards, maxLevels, progressReady],
  );

  useEffect(() => {
    if (!staticData?.battlepass) return;
    preloadBattlepassImages(
      staticData.battlepass,
      staticData.freeRewards,
      staticData.premiumRewards,
      resolveMediaUrl,
    );
  }, [staticData]);

  const userLevel = progressData?.userLevel ?? 0;
  const userExp = progressData?.userExp ?? 0;
  const userIsPremium = progressData?.userIsPremium ?? 0;
  const userGender = progressData?.userGender ?? null;

  const scrollCarousel = (dir: -1 | 1) => {
    const el = carouselRef.current;
    if (!el) return;
    const col = el.querySelector<HTMLElement>('.battlepass-carousel-col');
    const step = col?.offsetWidth ?? 70;
    el.scrollBy({ left: dir * step * 3, behavior: 'smooth' });
  };

  const scrollToLevel = (level: number) => {
    const el = carouselRef.current;
    if (!el) return;
    const col = el.querySelector<HTMLElement>(`[data-bp-level="${level}"]`);
    if (!col) return;
    const left = col.offsetLeft - el.clientWidth / 2 + col.offsetWidth / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  };

  const handleClaim = useCallback(
    async (rewardIds: number[]): Promise<boolean> => {
      if (rewardIds.length === 0) return false;
      try {
        const { results, allSuccess } = await claimMany(rewardIds);
        if (allSuccess) {
          showFlash(t('bp_reward_success'), 'success');
          return true;
        }
        const firstError = results.find((r) => !r.success);
        showFlash(firstError?.message || t('bp_claim_error'), 'danger');
        return false;
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
            : '';
        showFlash(msg || t('bp_claim_error'), 'danger');
        return false;
      }
    },
    [claimMany, showFlash, t],
  );

  const handleBuyPremium = async () => {
    if (!silkDialog) return;
    setSilkDialog({ ...silkDialog, phase: 'pending', message: t('silk_confirm_processing_msg') });
    try {
      const result = await buyPremium.mutateAsync();
      setSilkDialog({
        action: 'premium',
        phase: result.success ? 'success' : 'error',
        message: result.message,
      });
      if (result.success) showFlash(result.message, 'success');
    } catch {
      setSilkDialog({ action: 'premium', phase: 'error', message: t('bp_claim_error') });
    }
  };

  const handleLevelUp = async () => {
    if (!silkDialog) return;
    setSilkDialog({ ...silkDialog, phase: 'pending', message: t('silk_confirm_processing_msg') });
    try {
      const result = await levelUp.mutateAsync();
      setSilkDialog({
        action: 'levelup',
        phase: result.success ? 'success' : 'error',
        message: result.message,
      });
      if (result.success) showFlash(result.message, 'success');
    } catch {
      setSilkDialog({ action: 'levelup', phase: 'error', message: t('bp_claim_error') });
    }
  };

  const silkModalConfig =
    silkDialog?.action === 'premium'
      ? {
          title: t('bp_premium_confirm_title'),
          message: t('bp_premium_confirm_msg'),
          cost: BP_PREMIUM_COST,
          confirmLabel: t('bp_buy_pass'),
          onConfirm: () => void handleBuyPremium(),
        }
      : silkDialog?.action === 'levelup'
        ? {
            title: t('bp_levelup_confirm_title'),
            message: t('bp_levelup_confirm_msg'),
            cost: BP_LEVELUP_COST,
            confirmLabel: t('bp_levelup_confirm_title'),
            onConfirm: () => void handleLevelUp(),
          }
        : null;

  if ((staticError || progressError) && !staticData && !progressData) {
    return (
      <div className="container-ingame fade-in battlepass-page">
        <div className="battlepass-container battlepass-error-msg">
          <SimpleMenu variant="minimal" />
          {t('bp_claim_error')}
        </div>
      </div>
    );
  }

  const loadingStatic = staticLoading && !staticData;
  const expPercent = progressReady ? Math.min(100, (userExp % 1000) / 10) : 0;
  const expRemainder = progressReady ? userExp % 1000 : 0;
  const fallbackBg = assetUrl('/assets/battlepass/chrome/fondo-fallback.jpg');
  // banner_img de campaña = fondo full-bleed (subir arte cinematico desde el panel).
  // Si la campaña aún tiene el strip viejo, se verá ese arte hasta reemplazarlo.
  const bannerCacheToken = bp
    ? (bp.banner_rev ?? bp.updated_at ?? `${bp.id}-${bp.banner_img}`)
    : '';
  const bannerSrc = bp?.banner_img
    ? resolveMediaUrl(bp.banner_img, bannerCacheToken)
    : fallbackBg;
  const chromePremium = assetUrl('/assets/battlepass/chrome/estandarte-premium.png?v=4');
  const chromeFree = assetUrl('/assets/battlepass/chrome/estandarte-free.png?v=4');
  const logoSrc = assetUrl('/assets/images/logos/republic-logo.png');
  const carouselLevels = maxLevels > 0 ? maxLevels : 8;
  const showCarousel = maxLevels > 0;
  const seasonId =
    bp?.battlepass_id != null && String(bp.battlepass_id).trim() !== ''
      ? String(bp.battlepass_id).padStart(2, '0')
      : null;

  const levelUpButton = bp ? (
    <button
      type="button"
      className={`battlepass-action-btn battlepass-action-btn--levelup${!progressReady ? ' battlepass-action-btn--hidden' : ''}`}
      title={`${t('bp_levelup_cta')} ${BP_LEVELUP_COST}s`}
      disabled={!progressReady || userLevel >= maxLevels || levelUp.isPending}
      tabIndex={progressReady ? 0 : -1}
      aria-hidden={!progressReady ? 'true' : undefined}
      onClick={() => setSilkDialog({ action: 'levelup', phase: 'confirm' })}
    >
      <span>+</span>
    </button>
  ) : null;

  return (
    <div className="container-ingame fade-in battlepass-page">
      <IngameFlash flash={flash} />

      <div className="battlepass-container battlepass-aaa">
        <div className="battlepass-aaa-bg" aria-hidden>
          <img
            key={bannerSrc}
            src={bannerSrc}
            alt=""
            className="battlepass-aaa-bg-img"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="battlepass-aaa-bg-overlay" />
        </div>

        <SimpleMenu variant="minimal" />

        <header className="battlepass-aaa-header">
          <div className="battlepass-aaa-header-left">
            <div className="battlepass-aaa-brand">
              <img src={logoSrc} alt="Republic" className="battlepass-aaa-logo" />
              <div className="battlepass-aaa-title-row">
                <span className="battlepass-aaa-flourish battlepass-aaa-flourish--left" aria-hidden />
                <h1 className="battlepass-aaa-title">
                  {bp ? bp.name : loadingStatic ? '…' : t('bp_no_active')}
                </h1>
                <span className="battlepass-aaa-flourish battlepass-aaa-flourish--right" aria-hidden />
              </div>
              {seasonId ? (
                <div className="battlepass-aaa-season">{t('bp_season', { id: seasonId })}</div>
              ) : null}
            </div>

            <div className="battlepass-aaa-brand-rule" aria-hidden />

            <div
              className={`battlepass-progress battlepass-progress--hero${progressReady ? '' : ' battlepass-progress--awaiting'}`}
            >
              <div className="battlepass-progress-headline">
                <span className="battlepass-level-hero">
                  {t('bp_level_hero', { level: progressReady ? userLevel : '—' })}
                </span>
                <span className="battlepass-level-sep" aria-hidden />
                {levelUpButton}
              </div>
              <div className="battlepass-expbar-stack">
                <div className="battlepass-expbar">
                  <div className="battlepass-expbar-track">
                    <div className="battlepass-expbar-particles" />
                    <div
                      className={`battlepass-expbar-fill${progressReady ? '' : ' battlepass-expbar-fill--awaiting'}`}
                      style={{ width: progressReady ? `${expPercent}%` : '0%' }}
                    />
                  </div>
                </div>
                <div className="battlepass-exp-text">
                  {progressReady ? `${expRemainder} / 1000 XP` : '— / 1000 XP'}
                </div>
                <div className="battlepass-premium-rule" aria-hidden />
                <button
                  type="button"
                  className={`battlepass-premium-cta${progressReady && userIsPremium ? ' is-owned' : ''}`}
                  disabled={!progressReady || Boolean(userIsPremium) || buyPremium.isPending}
                  title={
                    progressReady && userIsPremium
                      ? t('bp_premium_active')
                      : t('bp_premium_confirm_title')
                  }
                  aria-label={
                    progressReady && userIsPremium
                      ? t('bp_premium_active')
                      : t('bp_premium_confirm_title')
                  }
                  onClick={() => setSilkDialog({ action: 'premium', phase: 'confirm' })}
                >
                  <i
                    className={`ph-light ${progressReady && userIsPremium ? 'ph-check' : 'ph-lock'}`}
                    aria-hidden
                  />
                  <span className="battlepass-premium-cta-sep" aria-hidden />
                  <span>{t('bp_track_premium')}</span>
                </button>
              </div>
            </div>
          </div>

          <div className="battlepass-aaa-header-right">
            <div className="battlepass-aaa-days-row">
              {bp ? (
                <div className="battlepass-aaa-days">
                  <i className="ph-light ph-hourglass-medium" aria-hidden />
                  <span>{formatDaysRemaining(bp.end_date, t)}</span>
                </div>
              ) : (
                <div className="battlepass-aaa-days" aria-hidden />
              )}
              <span className="battlepass-aaa-days-sep" aria-hidden />
              <button
                type="button"
                className="battlepass-aaa-info"
                onClick={() => setExpModalOpen(true)}
                title={t('bp_exp_info_title')}
                aria-label={t('bp_exp_info_title')}
              >
                <i className="ph-light ph-info" aria-hidden />
              </button>
            </div>
          </div>
        </header>

        <div className="battlepass-aaa-body">
          <div className="battlepass-aaa-center">
            <div className="battlepass-tracks-shell">
              <div className="battlepass-estandartes" aria-hidden={!bp}>
                <div className="bp-level-diamond-wrapper battlepass-estandarte-level-spacer" aria-hidden>
                  <span className="bp-level-number">10</span>
                  <div className="bp-level-rail">
                    <span className="bp-level-diamond" />
                  </div>
                </div>
                <div className="battlepass-carousel-cell battlepass-estandarte-cell-spacer" aria-hidden>
                  <div className="battlepass-carousel-cell-imgbox" />
                  <div className="battlepass-reward-status" />
                </div>
                <div className="battlepass-estandarte-origin">
                  <div className="battlepass-estandarte battlepass-estandarte--premium">
                    <img src={chromePremium} alt={t('bp_track_premium')} />
                  </div>
                  <div className="battlepass-estandarte battlepass-estandarte--free">
                    <img src={chromeFree} alt={t('bp_track_free')} />
                  </div>
                </div>
                <div className="battlepass-carousel-cell battlepass-estandarte-cell-spacer" aria-hidden>
                  <div className="battlepass-carousel-cell-imgbox" />
                  <div className="battlepass-reward-status" />
                </div>
              </div>

              <div className="battlepass-tracks-main">
              <div
                className={`battlepass-carousel-wrapper${progressInitialLoad ? ' battlepass-carousel-wrapper--awaiting' : ''}${progressReady ? ' battlepass-carousel-wrapper--ready' : ''}`}
              >
                {!showCarousel ? (
                  <BattlepassSkeletonCarousel cols={carouselLevels} />
                ) : (
                  <div className="battlepass-carousel" ref={carouselRef}>
                    {Array.from({ length: maxLevels }, (_, i) => i + 1).map((level) => {
                      const free = freeRewards[level];
                      const premium = premiumRewards[level];
                      const premiumCellClass =
                        progressReady && userIsPremium ? 'premium-effect' : progressReady ? 'locked' : '';

                      return (
                        <div className="battlepass-carousel-col" data-bp-level={level} key={level}>
                          <div
                            className={`bp-level-diamond-wrapper${progressReady && level === userLevel ? ' is-current' : ''}`}
                          >
                            <span className="bp-level-number">{level}</span>
                            <div className="bp-level-rail" aria-hidden>
                              <span className="bp-level-diamond" />
                            </div>
                          </div>
                          <RewardCell
                            reward={premium ?? null}
                            cellClass={`battlepass-carousel-cell--premium ${premiumCellClass}`}
                            userGender={userGender}
                            progressReady={progressReady}
                            onClaim={handleClaim}
                          />
                          <div className="bp-track-separator" aria-hidden>
                            <span className="bp-track-separator-diamond" />
                          </div>
                          <RewardCell
                            reward={free ?? null}
                            cellClass="battlepass-carousel-cell--free"
                            userGender={userGender}
                            progressReady={progressReady}
                            onClaim={handleClaim}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {showCarousel ? (
                <div className="battlepass-level-pager">
                  <button type="button" className="battlepass-pager-arrow" onClick={() => scrollCarousel(-1)} aria-label="Previous">
                    <i className="ph-light ph-caret-double-left" aria-hidden />
                  </button>
                  {Array.from({ length: maxLevels }, (_, i) => i + 1).map((level) => (
                    <button
                      type="button"
                      key={level}
                      className={`battlepass-pager-level${progressReady && level === userLevel ? ' is-current' : ''}`}
                      onClick={() => scrollToLevel(level)}
                    >
                      <span>{level}</span>
                    </button>
                  ))}
                  <button type="button" className="battlepass-pager-arrow" onClick={() => scrollCarousel(1)} aria-label="Next">
                    <i className="ph-light ph-caret-double-right" aria-hidden />
                  </button>
                </div>
              ) : null}
              </div>
            </div>
          </div>
        </div>

        <BattlepassExpModal
          className="silk-modal--aaa"
          open={expModalOpen}
          onClose={() => setExpModalOpen(false)}
        />

        {silkDialog && silkModalConfig && (
          <SilkConfirmModal
            className="silk-modal--aaa"
            open
            phase={silkDialog.phase}
            title={silkModalConfig.title}
            message={silkModalConfig.message}
            cost={silkModalConfig.cost}
            balance={silkDialog.phase === 'confirm' && silkLoading ? null : (silkBalance ?? null)}
            balanceLoading={silkDialog.phase === 'confirm' && silkLoading}
            balanceError={silkDialog.phase === 'confirm' && silkError}
            resultMessage={silkDialog.message}
            confirmLabel={silkModalConfig.confirmLabel}
            onConfirm={silkModalConfig.onConfirm}
            onClose={() => setSilkDialog(null)}
          />
        )}
      </div>
    </div>
  );
}
